import {storeToRefs} from 'pinia';
import {useShoppingListsStore} from '~/stores/shoppingLists';
import type {
	Gsoa_ProductCatalog_ProductDetail as ProductDetail,
	Gsoa_ShoppingList_UpdateItem as UpdateItem,
} from '~/openapi';
import {matchesShoppingListItemData} from '~/utils/shoppingList';
import type {ShoppingListAddMethod} from '~/utils/tracking';

interface UseShoppingListProductOptions {
	method?: ShoppingListAddMethod;
}

export default function useShoppingListProduct(
	product: MaybeRefOrGetter<ProductDetail | undefined>,
	options: UseShoppingListProductOptions = {},
) {
	const shoppingListsStore = useShoppingListsStore();
	const {selectedShoppingList, shoppingListsItems} = storeToRefs(shoppingListsStore);
	const {pending, handler} = usePending();

	const productRef = computed(() => toValue(product));
	const selectedListId = computed(() => selectedShoppingList.value);

	const currentItem = computed(() => {
		const product = productRef.value;
		if (!product || selectedListId.value == null) {
			return undefined;
		}

		const listItems = shoppingListsItems.value
			.find((item) => item.id === selectedListId.value)
			?.products.Items;

		if (!listItems) {
			return undefined;
		}

		if (product.vanr) {
			return listItems.find((item) => item.VANR === product.vanr);
		}

		const text = product.name ?? '';
		if (!text) {
			return undefined;
		}

		return listItems.find((item) => matchesShoppingListItemData(item, {Text: text}));
	});

	const amount = computed(() => currentItem.value?.Amount ?? 0);
	const isInSelectedList = computed(() => !!currentItem.value);

	async function add() {
		const currentProduct = productRef.value;
		if (!currentProduct) {
			return;
		}
		await handler(async () => {
			const itemData = currentProduct.vanr
				? {
					Text: currentProduct.name ?? '',
					Amount: 1,
					VANR: currentProduct.vanr,
				} as UpdateItem
				: {
					Text: currentProduct.name ?? '',
					Amount: 1,
				} as UpdateItem;

			try {
				await shoppingListsStore.createNewItem(
					String(selectedListId.value ?? ''),
					itemData,
					currentProduct.vanr ? currentProduct : undefined,
				);
				if (options.method) {
					const listId = selectedListId.value;
					trackShoppingListAddItem({
						product: currentProduct.vanr ? currentProduct : undefined,
						method: options.method,
						itemType: currentProduct.vanr ? 'product' : 'text',
						listId,
						uniqItems: listId != null ? shoppingListsStore.getListActiveItemsCount(listId) : 0,
					});
				}
			} catch (e: any) {
				throw createError({
					message: 'Failed to add item to shopping list',
					data: {itemData, shoppingListId: selectedListId.value},
					cause: e,
				});
			}
		});
	}

	const sendAmount = useDebounceFn(async (listId: string, itemId: number, body: UpdateItem) => {
		await shoppingListsStore.setAmountShoppingListItem(listId, itemId, body);
	}, 500);

	function setAmount(value: number) {
		const item = currentItem.value;
		if (!item || selectedListId.value == null) {
			return;
		}
		item.Amount = value;
		sendAmount(String(selectedListId.value), item.ID, {
			Text: item.Text,
			Amount: value,
		});
	}

	async function remove() {
		const item = currentItem.value;
		if (!item || selectedListId.value == null) {
			return;
		}
		await handler(async () => {
			try {
				await shoppingListsStore.deleteShoppingListItem(String(selectedListId.value), String(item.ID));
			} catch (e: any) {
				throw createError({
					message: 'Failed to delete item from shopping list',
					data: {item, shoppingListId: selectedListId.value},
					cause: e,
				});
			}
		});
	}

	return {
		amount,
		currentItem,
		isInSelectedList,
		pending,
		add,
		setAmount,
		remove,
	};
}
